import type {ReactElement, RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {AiGenerationPopoverProps} from './ai-generation-popover.types';
import styles from './ai-generation-popover.module.scss';
import {AIButtonShape, AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Size} from '@Components/button';
import {AiButton} from '@Components/ai-button';
import {Popover} from '@Components/popover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {AiGenerationBox} from '@Components/ai-generation-box';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {openAiGenerationModal} from '@Modals/ai-generation-modal/ai-generation-modal.types';

export function AiGenerationPopover<T>({
  className,
  closePopoverOnItemSelect = false,
  alignment = PopoverDefaultAlignment.RIGHT_CENTER,
  referenceElementRef,
  isOpen,
  onClose,
  isLoading = false,
  loadingStateText,
  ...aiGenerationBoxProps
}: AiGenerationPopoverProps<T>): ReactElement {
  const isControlled = referenceElementRef !== undefined;
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const internalButtonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);

  const popoverOpen = isControlled ? !!isOpen : internalOpen;
  const anchorRef: RefObject<HTMLButtonElement | null> = referenceElementRef ?? internalButtonRef;
  const hasHandedOffToModalRef = useRef<boolean>(false);

  const closePopover = (): void => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const openModalForMobile = (): void => {
    openAiGenerationModal<T>({
      ...aiGenerationBoxProps,
      closeOnItemSelect: closePopoverOnItemSelect,
      title: window.i18next.t('pmwjs_write_with_ai'),
      isMobile: true,
    });
  };

  useEffect(() => {
    if (!isControlled || !isMobile) {
      return;
    }

    if (popoverOpen && !hasHandedOffToModalRef.current) {
      hasHandedOffToModalRef.current = true;
      openModalForMobile();
      onClose?.();
    } else if (!popoverOpen) {
      hasHandedOffToModalRef.current = false;
    }
  }, [isControlled, popoverOpen, isMobile]);

  const handleButtonClick = (): void => {
    if (isLoading) {
      return;
    }

    if (isMobile) {
      openModalForMobile();
      return;
    }

    setInternalOpen((prev) => !prev);
  };

  const handleItemSelect = (item: T, prompt: string): void => {
    aiGenerationBoxProps.onItemSelect(item, prompt);

    if (closePopoverOnItemSelect) {
      closePopover();
    }
  };

  const renderPopover = (): ReactElement | null => {
    if (!popoverOpen || isMobile) {
      return null;
    }

    return (
      <Popover
        referenceElementRef={anchorRef}
        popoverCloseFunction={closePopover}
        alignment={alignment}
        hasArrow
        hasAutoPlacement={false}
        renderInPortal
        scrollAnchorIntoView={false}
        staticOffsetForArrow={0}
        className={styles.popup}
      >
        <AiGenerationBox {...aiGenerationBoxProps} onItemSelect={handleItemSelect} />
      </Popover>
    );
  };

  const renderTrigger = (): ReactElement | null => {
    if (isControlled) {
      return null;
    }

    return (
      <AiButton
        buttonRef={internalButtonRef}
        className={className}
        onClick={handleButtonClick}
        variant={AIButtonVariant.SUBDUED}
        shape={AIButtonShape.ROUND}
        size={Size.XSMALL}
        text={window.i18next.t('pmwjs_help_me_write')}
        icon="icon-ai-caption"
        isLoading={isLoading}
        loadingStateText={loadingStateText}
        customClasses={`spacing-p-l-3 spacing-p-r-3 spacing-p-t-2 spacing-p-b-2 ${internalOpen && !isMobile ? styles.aiButtonClicked : ''}`}
      />
    );
  };

  return (
    <>
      {renderTrigger()}
      {renderPopover()}
    </>
  );
}
